import React, { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import type { AssetOrInventory, EmployeeAssignmentOption, JobTreadJobOption } from './types'
import { getImageUrls, jobOptionFromItem, locationAddressFromJob } from './types'
import PhotoUploadPreview from './PhotoUploadPreview'
import JobSearchField from './JobSearchField'
import { useAutoDismiss } from './useAutoDismiss'

type AssetFormFieldsProps = {
  item: AssetOrInventory
  employeeOptions: EmployeeAssignmentOption[]
  saving?: boolean
  onUpdateField: (key: keyof AssetOrInventory, value: unknown) => void
  onUpdateLocationAddress: (address: string) => void
  onApplyJobLocation?: (job: JobTreadJobOption | null) => void
  onUploadImages: (files: FileList | null) => void | Promise<void>
  onAddImageDataUrls?: (dataUrls: string[]) => void
  onRemoveImage: (idx: number) => void
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </div>
  )
}

export const AssetFormFields: React.FC<AssetFormFieldsProps> = ({
  item,
  employeeOptions,
  saving = false,
  onUpdateField,
  onUpdateLocationAddress,
  onApplyJobLocation,
  onUploadImages,
  onAddImageDataUrls,
  onRemoveImage,
}) => {
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const images = getImageUrls(item)
  const selectedJob = useMemo(() => jobOptionFromItem(item), [item])
  useAutoDismiss(photoError, () => setPhotoError(null))

  const handlePhotos = async (files: FileList | null) => {
    if (!files?.length) return
    setPhotoBusy(true)
    setPhotoError(null)
    try {
      await onUploadImages(files)
    } catch (err: unknown) {
      const msg = err instanceof Error && err.message ? err.message : ''
      setPhotoError(
        /memory|allocate|quota/i.test(msg)
          ? 'Not enough memory to process that photo. Choose a smaller image from the gallery.'
          : msg || 'Could not process photo. Try a smaller image from the gallery.',
      )
    } finally {
      setPhotoBusy(false)
    }
  }

  const handleJobChange = (job: JobTreadJobOption | null) => {
    if (onApplyJobLocation) {
      onApplyJobLocation(job)
      return
    }
    onUpdateField('jobId', job?.id || '')
    onUpdateField('jobNumber', job?.number || '')
    onUpdateField('jobName', job?.name || '')
    onUpdateLocationAddress(locationAddressFromJob(job))
  }

  return (
    <div className="w-full max-w-md mx-auto flex flex-col gap-4">
      <section className="rounded-box border border-base-200 bg-base-200/30 p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm">Photos</h4>
          {photoBusy ? <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary" /> : null}
        </div>
        <PhotoUploadPreview
          images={images}
          alt={item.name || 'Asset'}
          disabled={saving || photoBusy}
          onUpload={handlePhotos}
          onAddDataUrls={onAddImageDataUrls}
          onRemove={onRemoveImage}
        />
        {photoError ? <p className="text-xs text-error mt-2">{photoError}</p> : null}
      </section>

      <section className="rounded-box border border-base-200 bg-base-200/30 p-3 space-y-3">
        <h4 className="font-semibold text-sm">Details</h4>
        <Field label="Name">
          <input
            className="input input-bordered w-full"
            value={item.name || ''}
            onChange={(e) => onUpdateField('name', e.target.value)}
            disabled={saving}
            placeholder="Asset name"
          />
        </Field>
        <Field label="Type">
          <input
            className="input input-bordered w-full"
            value={item.type || ''}
            onChange={(e) => onUpdateField('type', e.target.value)}
            disabled={saving}
            placeholder="e.g. vehicle, equipment"
          />
        </Field>
        <Field label="Serial number">
          <input
            className="input input-bordered w-full"
            value={item.serialNumber || ''}
            onChange={(e) => onUpdateField('serialNumber', e.target.value)}
            disabled={saving}
          />
        </Field>
        <Field label="Status">
          <input
            className="input input-bordered w-full"
            value={item.status || ''}
            onChange={(e) => onUpdateField('status', e.target.value)}
            disabled={saving}
            placeholder="active"
          />
        </Field>
        <Field label="Value">
          <input
            type="number"
            className="input input-bordered w-full"
            value={item.value ?? 0}
            onChange={(e) => onUpdateField('value', Number(e.target.value))}
            disabled={saving}
          />
        </Field>
      </section>

      <section className="rounded-box border border-base-200 bg-base-200/30 p-3 space-y-3">
        <h4 className="font-semibold text-sm">Assignment</h4>
        <Field label="Assigned employee">
          <select
            className="select select-bordered w-full"
            value={item.assignedEmployeeId || ''}
            onChange={(e) => onUpdateField('assignedEmployeeId', e.target.value)}
            disabled={saving}
          >
            <option value="">Unassigned</option>
            {employeeOptions.map((employee) => (
              <option key={employee.employeeId} value={employee.employeeId}>
                {employee.displayName} ({employee.employeeId})
              </option>
            ))}
          </select>
        </Field>
        <JobSearchField
          label="Location"
          value={selectedJob}
          onChange={handleJobChange}
          placeholder="Search JobTread locations..."
          disabled={saving}
        />
        {item.location?.address ? (
          <p className="text-xs text-base-content/60">Site: {item.location.address}</p>
        ) : null}
      </section>

      <section className="rounded-box border border-base-200 bg-base-200/30 p-3 space-y-3">
        <h4 className="font-semibold text-sm">Notes</h4>
        <textarea
          className="textarea textarea-bordered w-full"
          value={item.notes || ''}
          onChange={(e) => onUpdateField('notes', e.target.value)}
          rows={3}
          disabled={saving}
          placeholder="Optional notes"
        />
      </section>
    </div>
  )
}

export default AssetFormFields
